// src/components/MenuModal.js
import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { flyToCart } from "../utils/flyToCart";
import {
  toggleOption,
  incrementQuantity,
  decrementQuantity,
  clearItem,
  incrementItemQuantity,
  decrementItemQuantity,
  setItemData
} from '../slices/itemSlice';
import MenuTags from "./MenuTags";
import { clearItemToEdit } from '../slices/cartSlice';

import './MenuModal.css';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSettings } from "../context/SettingsContext";
import { Clock, Share2 } from "lucide-react";
import { encodeId } from "../utils/encode";

const copyToClipboard = async (text) => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    return true;
  } catch {
    return false;
  }
};


const getTotalOptionQty = (groupId, selections) => {
  const opts = selections[groupId] || {};
  return Object.values(opts).reduce((sum, qty) => sum + qty, 0);
};


const MenuModal = ({ onClose, isEditing = false }) => {
  const dispatch = useDispatch();
  const itemState = useSelector((state) => state.item);
  const itemToEdit = useSelector((state) => state.cart.itemToEdit);
const [openGroupId, setOpenGroupId] = useState(null);

  const addBtnRef = useRef(null);
  const cartIconEl = document.querySelector(".cart-icon");
  const { settings } = useSettings();
  const isOptionPreselected = (group, option) => {
  return option?.preselected == 1 || option?.preselected === true;
};


  // ✏️ Special Instruction
  const [specialInstruction, setSpecialInstruction] = useState("");

  // preload note when editing
  useEffect(() => {
    if (isEditing && itemToEdit?.specialInstruction) {
      setSpecialInstruction(itemToEdit.specialInstruction);
    }
  }, [isEditing, itemToEdit]);

  // preload modifiers when editing
  useEffect(() => {
    if (isEditing && itemToEdit) {
      dispatch(setItemData({
        item: itemToEdit,
        selections: itemToEdit.selectedModifiers.reduce((acc, group) => {
          acc[group.groupId] = group.options.reduce((optAcc, option) => {
            optAcc[option.optionId] = option.quantity;
            return optAcc;
          }, {});
          return acc;
        }, {}),
        quantity: itemToEdit.quantity
      }));
    }
  }, [isEditing, itemToEdit, dispatch]);

  const item = itemState?.item;
  const modifiers = itemState?.modifiers || [];
  const selections = itemState?.selections || {};
  const quantity = itemState?.quantity || 1;

  const basePrice = parseFloat(item?.basePrice || item?.price || 0);


  const handleShareMenu = async (e) => {
  e.stopPropagation();

  const params = new URLSearchParams({
    menu: encodeId(item.id)
  });

  const url = `${window.BASE_URL}/menu-app?${params.toString()}`;
  const shareData = {
    title: item.name,
    text: `Check out this item: ${item.name}`,
    url,
  };

  // ✅ Native Share (WhatsApp, Messages, etc.)
  if (navigator.share) {
    try {
      await navigator.share(shareData);
    } catch (err) {
      console.log("Share cancelled", err);
    }
  } 
  // 🧯 Fallback: Copy to clipboard
  else {
    const success = await copyToClipboard(url);
    success
      ? toast.success("Menu link copied to clipboard")
      : toast.error("Failed to copy link");
  }
};



  /**
   * 🟢 Auto-preselect options when item loads (add mode only)
   */
  useEffect(() => {
    if (!modifiers.length || !item || isEditing) return;

    const groupsToOpen = {};

    modifiers.forEach(group => {
      const preselectedOptions = group.options?.filter(
        (opt) => opt?.preselected === 1 || opt?.preselected === true
      );

      if (preselectedOptions.length > 0) {
        groupsToOpen[group.id] = true; // auto-open group
      }

      preselectedOptions.forEach((option) => {
        // toggle selection on
        dispatch(
          toggleOption({
            groupId: group.id,
            optionId: option.id,
            maxSelect: group.max_select,
            singleSelect: group.max_select === 1 && group.min_select === 1,
          })
        );

        // set quantity = 1
        dispatch(
          incrementQuantity({
            groupId: group.id,
            optionId: option.id,
          })
        );
      });
    });

    // open groups that had preselected options
    // if (Object.keys(groupsToOpen).length > 0) {
    //   setOpenGroups(prev => ({ ...prev, ...groupsToOpen }));
    // }

  }, [item, modifiers, isEditing, dispatch]);

  // calculate modifier totals
  let modifiersTotal = 0;
  Object.entries(selections).forEach(([groupId, options]) => {
    const group = modifiers.find((g) => g.id === groupId);
    if (!group) return;
    Object.entries(options).forEach(([optionId, qty]) => {
      const option = group.options.find((o) => o.id === optionId);
      if (option) {
        const optionPrice = parseFloat(option.price) || 0;
        modifiersTotal += optionPrice * qty;
      }
    });
  });

  const subtotal = basePrice + modifiersTotal;
  const total = subtotal * quantity;

 const toggleAccordion = (groupId) => {
  setOpenGroupId((prev) => (prev === groupId ? null : groupId));
};

const handleToggle = (group, option) => {
  const selectedGroupOptions = selections[group.id] || {};

  // ✅ declare FIRST
  const isSelected = !!selectedGroupOptions[option.id];

  const totalQty = Object.values(selectedGroupOptions).reduce(
    (sum, qty) => sum + qty,
    0
  );

  // 🚫 block only when turning ON and max reached
  if (!isSelected && totalQty >= group.max_select) {
    toast.warn(`You cannot select more option(s) in "${group.name}"`);
    return;
  }

  dispatch(
    toggleOption({
      groupId: group.id,
      optionId: option.id,
      maxSelect: group.max_select,
      singleSelect: group.max_select === 1 && group.min_select === 1,
    })
  );
};


  const getHeaderLabel = (group) => {

    switch (group.type) {
      case 1:
        return `${group.name} (Upto ${group.max_select})`;
      case 2:
        return `${group.name} (Must Select ${group.max_select})`;
      case 4:
        return `${group.name} (Preselected)`;
      case 3:
        return `${group.name} (Optional Upto ${group.max_select})`;
      default:
        return `${group.name} (Optional)`;
    }
  };

  const isGroupSelected = (group) => {
    const groupSelections = selections[group.id] || {};
    const selectedQty = getTotalOptionQty(group.id, selections);

if (group.type === 2) return selectedQty >= group.max_select;
if (group.type === 1 && group.min_select > 0)
  return selectedQty >= group.min_select;
return selectedQty > 0;

  };

  const handleSaveToCart = () => {
    for (let group of modifiers) {
      const selected = selections[group.id] || {};
      const selectedQty = getTotalOptionQty(group.id, selections);


      // if (group.type === 2 && selectedCount < group.max_select) {
      //   toast.error(`Please select ${group.max_select} option(s) in "${group.name}"`);
      //   return;
      // }

      // if (group.type === 1 && group.min_select > 0 && selectedCount < group.min_select) {
      //   toast.error(`Please select at least ${group.min_select} option(s) in "${group.name}"`);
      //   return;
      // }

      if (group.type === 2 && selectedQty < group.max_select) {
        toast.error(
          `Please select ${group.max_select} option(s) in "${group.name}"`
        );
        return;
      }

      if (group.type === 1 && group.min_select > 0 && selectedQty < group.min_select) {
        toast.error(
          `Please select at least ${group.min_select} option(s) in "${group.name}"`
        );
        return;
      }

    }

    const selectedModifiers = modifiers.map((group) => {
      const selectedOptions = selections[group.id] || {};
      const options = Object.entries(selectedOptions).map(([optionId, qty]) => {
        const option = group.options.find((o) => o.id === optionId);
        return {
          optionId: option.id,
          name: option.name,
          price: parseFloat(option.price),
          quantity: qty,
        };
      });

      return {
        groupId: group.id,
        groupName: group.name,
        options,
      };
    }).filter(group => group.options.length > 0);


    // ---------- PRESELECTED OPTION HANDLING ----------
let removedPreselectedNotes = [];

// Detect preselected options removed by user
modifiers.forEach(group => {
  group.options.forEach(option => {
    const wasPreselected = option?.preselected == 1 || option?.preselected === true;
    const isSelectedNow = selections[group.id]?.[option.id] ? true : false;

    if (wasPreselected && !isSelectedNow) {
      removedPreselectedNotes.push(`[No ${option.name}]`);
    }
  });
});

// Current instruction
let newInstruction = specialInstruction || "";

// Remove old "[No ...]" only in edit mode
if (isEditing && newInstruction) {
  newInstruction = newInstruction.replace(/\[No [^\]]+\]/g, "").trim();
}

// Add new missing preselected notes
if (removedPreselectedNotes.length > 0) {
  newInstruction =
    (newInstruction ? newInstruction + " " : "") +
    removedPreselectedNotes.join(" ");
}

// Update final special instruction in state
setSpecialInstruction(newInstruction);


    const cartItem = {
      id: Date.now(),
      itemId: item.id,
      type: item.type,
      name: item.name,
      image: item.image,
      basePrice,
      quantity,
      total,
      selectedModifiers,
      specialInstruction: newInstruction,
      exclusion: item.exclusion,
    };

    const existingCart = JSON.parse(localStorage.getItem('cart')) || [];
    let updatedCart;

    if (isEditing) {
      updatedCart = existingCart.map((ci) => (ci.id === itemToEdit.id ? cartItem : ci));
      toast.success('Item updated in cart!');
    } else {

      if (item.type == 'free_item') {
        const maxFreeItems = Number(settings?.offer_item || 0);
        const existingCart = JSON.parse(localStorage.getItem("cart")) || [];
        const freeItemCount = existingCart.filter(ci => ci.type == 'free_item').length;
        if (freeItemCount >= maxFreeItems) {
          toast.warn(`⚠️ You can only add up to ${maxFreeItems} free item${maxFreeItems > 1 ? "s" : ""}.`);
          return;
        }
      }

      if (item.type == 'first_offer') {
        const maxFreeItems = Number(settings?.welcome_item || 0);
        const existingCart = JSON.parse(localStorage.getItem("cart")) || [];
        const freeItemCount = existingCart.filter(ci => ci.type == 'first_offer').length;
        if (freeItemCount >= maxFreeItems) {
          toast.warn(`⚠️ You can only add up to ${maxFreeItems} first order offer item${maxFreeItems > 1 ? "s" : ""}.`);
          return;
        }
      }

      updatedCart = [...existingCart, cartItem];
      toast.success('Item added to cart!');
    }

    if (addBtnRef.current && cartIconEl) {
      flyToCart(item.image, addBtnRef.current, cartIconEl);
    }

    // Store time only if this is the first item in cart
        const cc = JSON.parse(localStorage.getItem('cart')) || [];
        if (cc.length === 0) {
          localStorage.setItem("cartCreatedAt", Date.now().toString());
        }

    localStorage.setItem('cart', JSON.stringify(updatedCart));
    // ✅ Update last activity ONLY when cart changes
localStorage.setItem("last_activity_time", Date.now().toString());
    dispatch(clearItem());
    dispatch(clearItemToEdit());
    onClose();
  };


  // 🔍 Check for cart type conflicts
const cartData = JSON.parse(localStorage.getItem("cart")) || [];

const hasCustomPrep = cartData.some(ci => ci.type == "custom_prep_time");
const hasNonCustomPrep = cartData.some(ci => ci.type != "custom_prep_time");

// Determine if current item violates the rule
let cartRestrictionMessage = "";

if (hasCustomPrep && item?.type !== "custom_prep_time") {
  cartRestrictionMessage = "Items with extended prep time cannot be added with standard items";
} else if (hasNonCustomPrep && item?.type === "custom_prep_time") {
  cartRestrictionMessage = "Items with extended prep time cannot be added with standard items";
}


  if (!item) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ padding: "0px" }}>
        <button className="close-btn" onClick={() => { dispatch(clearItem()); dispatch(clearItemToEdit()); onClose(); }}>
          <span className="close-icon">&times;</span>
        </button>

      <div
  className="food-image"
  style={{
    backgroundImage: `url(${item.image})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    width: "100%",
    height: "220px",
    position: "relative",
  }}
>
  {/* 🔗 Share Icon */}
  <div
    onClick={handleShareMenu}
    title="Copy menu link"
    style={{
      position: "absolute",
      top: "10px",
      left: "10px",
      background: "rgba(255,255,255,0.9)",
      borderRadius: "50%",
      padding: "8px",
      cursor: "pointer",
      boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
      zIndex: 2,
    }}
  >
    <Share2 size={18} color="#111" />
  </div>

  {/* ⏱ Existing Custom Prep Icon */}
  {item?.type === "custom_prep_time" && (
    <div className="absolute top-2 left-12 group">
      <div className="bg-white/90 p-1.5 rounded-full shadow-md">
        <Clock size={20} className="text-orange-600" />
      </div>
      <div
        className="absolute left-0 mt-1 w-max opacity-0 group-hover:opacity-100
        transition-opacity duration-200 bg-gray-800 text-white text-xs
        rounded px-2 py-1"
      >
        This item has extended prep time
      </div>
    </div>
  )}
</div>


          {item?.type == "custom_prep_time" && (
            <div className="absolute top-2 left-2 group">
              <div className="bg-white/90 p-1.5 rounded-full shadow-md">
                <Clock size={20} className="text-orange-600" />
              </div>
              <div
                className="absolute left-0 mt-1 w-max opacity-0 group-hover:opacity-100 
                          transition-opacity duration-200 bg-gray-800 text-white text-xs 
                          rounded px-2 py-1"
              >
                This item has extended prep time
              </div>
            </div>
          )}

        <div className="mdiv" style={{ padding: "20px" }}>
          <h2 className="pricehheader2"  style={{ color: settings?.cart_title_color || "#111827" }}>
            {item.name} 
           <span className="priceh2">
  {item?.is_special == 1 ? (
    <>
      <span style={{ textDecoration: "line-through", marginRight: "6px", color: "#999" }}>
        {localStorage.getItem("currency")}{" "}
        {parseFloat(item?.display_price).toFixed(2)}
      </span>

      <span style={{  fontWeight: "600" }}>
        {localStorage.getItem("currency")}{" "}
        {parseFloat(item?.price).toFixed(2)}
      </span>
    </>
  ) : (
    <>
      {localStorage.getItem("currency")}{" "}
      {parseFloat(item?.display_price).toFixed(2)}
    </>
  )}
</span>

          </h2>

          <p className="desc">{item.desc} 

          {item?.contains &&  <><br/><span style={{fontWeight:'700'}}> {item.contains} </span></>

          }
 </p>
          <MenuTags tags={item?.menu_tags} />

          {modifiers.map((group) => {
            const selectedGroup = selections[group.id] || {};
            const isOpen = openGroupId === group.id;

            return (
              <div key={group.id} className="accordion-group">
                <div className="accordion-header" onClick={() => toggleAccordion(group.id)}>
                  <h4>
                    {getHeaderLabel(group)}
                    {isGroupSelected(group) && (
                      <span className="cm-tick-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                          fill="none" stroke="white" strokeWidth="3"
                          strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                    )}
                  </h4>
                  <span className={`arrow ${isOpen ? 'open' : ''}`}>&#9660;</span>
                </div>

                {isOpen && (
                  <div className="accordion-body">
                    {group.options.map((option) => {
                      const isSelected = !!selectedGroup[option.id];
                      const qty = selectedGroup[option.id] || 0;

                      const totalQty = getTotalOptionQty(group.id, selections);
const isMaxReached = totalQty >= group.max_select;

const disableQtyPlus = isSelected && isMaxReached;

const disableToggle = !isSelected && isMaxReached;

                      return (
                        <div key={option.id} className="option-table-row">
                          <div className="col option-name">{option.name}</div>
                          <div className="col option-price">
                            
                            {localStorage.getItem('currency')} {parseFloat(option.price).toFixed(2)}
                          </div>

                          {(isSelected &&  option.quantity == 1)&&(
                            <div className="col option-qty">
                              <div className="qty-controls">
                                <button
                                  onClick={() => dispatch(decrementQuantity({ groupId: group.id, optionId: option.id }))}
                                  disabled={qty <= 1}
                                >
                                  −
                                </button>
                                <span>{qty}</span>
                                <button
                                  onClick={() => dispatch(incrementQuantity({ groupId: group.id, optionId: option.id }))}
                                    disabled={disableQtyPlus}
  style={{
    opacity: disableQtyPlus ? 0.5 : 1,
    cursor: disableQtyPlus ? "not-allowed" : "pointer",
  }}
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          )}

                          <div className="col toggle-switch">
                            <label className="switch">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggle(group, option)}
                                disabled={disableToggle}
                              />
                              <span className="slider round"></span>
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          <div className="total pricehheader2" style={{ marginTop: '20px', textAlign: 'right', fontSize: '18px' }}>
            <strong  style={{ color: settings?.cart_title_color || "#111827" }}>Subtotal:  {localStorage.getItem('currency')} {subtotal.toFixed(2)}</strong>
          </div>

          <div className="quantity-total-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
            <div className="quantity-controls">
              <button onClick={() => dispatch(decrementItemQuantity())} disabled={quantity <= 1}>−</button>
              <span style={{ margin: '0 12px' }}>{quantity}</span>
              <button onClick={() => dispatch(incrementItemQuantity())} disabled={(item?.type == 'normal' || item?.type == 'custom_prep_time') ? false : true}>+</button>
            </div>
            <div className="pricehheader2" style={{ float: 'right' }}>
              <strong  style={{ color: settings?.cart_title_color || "#111827" }}>Total:  {localStorage.getItem('currency')} {total.toFixed(2)}</strong>
            </div>
          </div>

          {/* ✏️ Special Instruction Section */}
          <div className="special-instruction-box" style={{
            marginTop: "20px", display: "flex", flexDirection: "column", gap: "8px"
          }}>
            <label htmlFor="special-instruction" style={{ fontWeight: "600", color: "#333", fontSize: "14px" }}>
              Special Instructions
            </label>
            <textarea
              id="special-instruction"
              value={specialInstruction}
              onChange={(e) => setSpecialInstruction(e.target.value)}
              placeholder="e.g. No onions, extra spicy, pack sauce separately..."
              style={{
                width: "100%", minHeight: "80px", resize: "vertical", borderRadius: "8px",
                border: "1px solid #ddd", padding: "10px", fontSize: "14px", color: "#333",
                fontFamily: "inherit", outline: "none", boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                transition: "border-color 0.2s ease, box-shadow 0.2s ease",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#f97316")}
              onBlur={(e) => (e.target.style.borderColor = "#ddd")}
            />
          </div>


        {cartRestrictionMessage ? (
  <div
    style={{
      backgroundColor: "#fff3cd",
      color: "#856404",
      padding: "12px 16px",
      borderRadius: "8px",
      textAlign: "center",
      marginTop: "16px",
      fontWeight: "500",
      border: "1px solid #ffeeba",
    }}
  >
    {cartRestrictionMessage}
  </div>
) : (
  <button className="add-cart-btn button-custom" 
style={{
  background: settings?.cart_btn_color || "#000000ff",
   color: settings?.cart_btn_text_color || "#ffffff",
}}
onMouseEnter={(e) => {
  e.currentTarget.style.background =
    settings?.cart_btn_hover || "#f59e0b";
}}
onMouseLeave={(e) => {
  e.currentTarget.style.background =
    settings?.cart_btn_color || "#ffffff";
}} ref={addBtnRef} onClick={handleSaveToCart}>
    {isEditing ? "UPDATE ITEM" : "ADD TO CART"}
  </button>
)}

        </div>
      </div>
    </div>
  );
};

export default MenuModal;
